'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import type { Language } from '@/lib/translations'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const updateLanguageDom = (lang: Language) => {
  // Update HTML attributes for RTL support
  const html = document.documentElement
  html.lang = lang
  // The font follows `dir` through --app-font in globals.css.
  html.dir = lang === 'ar' ? 'rtl' : 'ltr'
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en')
  const [mounted, setMounted] = useState(false)

  // Initialize language from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('alnajah-lang') as Language | null
    const preferred = stored || (navigator.language.startsWith('ar') ? 'ar' : 'en')
    setLanguageState(preferred)
    updateLanguageDom(preferred)
    setMounted(true)
  }, [])

  // Update DOM when language changes
  useEffect(() => {
    if (mounted) {
      updateLanguageDom(language)
    }
  }, [language, mounted])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem('alnajah-lang', lang)
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}
